import { DateTime } from "luxon";

// Les refus de l'API sur la règle de récurrence arrivent sous `slotCalendarConfig` :
// par champ (`{ slotCalendarConfig: { interval: "…" } }`) quand c'est la règle qui
// refuse, en une seule chaîne (`{ slotCalendarConfig: "…" }`) quand c'est le format
// de la série. Les contrôles lisent leur erreur par leur nom : on leur donne les
// champs, et le message sans champ s'affiche sous le bloc de répétition.
export function erreursRecurrence(errors) {
  const serie = errors && errors.slotCalendarConfig;
  if (!serie) return { champs: {}, message: null };
  if (typeof serie === "string") return { champs: {}, message: serie };
  return { champs: serie, message: null };
}

// L'API n'ouvre jamais de créneau le samedi ni le dimanche (CE-1392). Les week-ends
// restent affichés (rendez-vous saisis à la volée, anciens créneaux), mais on n'y
// ouvre rien.
export function estJourOuvre(date) {
  return DateTime.fromJSDate(new Date(date)).weekday <= 5;
}

// Un box supprimé (archivé côté API) ne reçoit plus de créneau : il n'est pas proposé
// pour un nouveau créneau, mais un créneau existant garde le sien, nommé comme tel.
export function boxesProposables(boxes, boxActuel) {
  return (boxes || [])
    .filter((b) => !b.isDeleted || b._id == boxActuel)
    .map((b) => (b.isDeleted ? { ...b, name: b.name + " (supprimé)" } : b));
}

// ---- Répétition d'un créneau : le calcul que fait l'API, pour l'aperçu de la modale ----

const NUMERO_JOUR = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };
const JOUR_DU_NUMERO = ["", "MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const NOMS_JOURS = { MO: "lundi", TU: "mardi", WE: "mercredi", TH: "jeudi", FR: "vendredi", SA: "samedi", SU: "dimanche" };

// Premier à quatrième, ou -1 pour le dernier : le cinquième n'existe pas tous les mois.
export const RANGS_DANS_MOIS = [
  { id: 1, name: "premier" },
  { id: 2, name: "deuxième" },
  { id: 3, name: "troisième" },
  { id: 4, name: "quatrième" },
  { id: -1, name: "dernier" },
];

export const JOURS_OUVRES = ["MO", "TU", "WE", "TH", "FR"].map((id) => ({ id, name: NOMS_JOURS[id] }));

const jour = (date) => DateTime.fromJSDate(new Date(date));

export const quantieme = (n) => (n === 1 ? "1er" : String(n));

export function jourSemaineDe(date) {
  return JOUR_DU_NUMERO[jour(date).weekday];
}

// Le rang proposé pour la date d'un créneau : « dernier » quand elle tombe dans les
// sept derniers jours du mois (le 26 novembre est le dernier jeudi, pas le quatrième).
export function rangDansMoisDe(date) {
  const d = jour(date);
  return d.day + 7 > d.daysInMonth ? -1 : Math.ceil(d.day / 7);
}

function jourDeRang(mois, numeroJour, rang) {
  if (rang > 0) {
    const premier = mois.startOf("month");
    return premier.plus({ days: ((numeroJour - premier.weekday + 7) % 7) + 7 * (rang - 1) });
  }
  const dernier = mois.endOf("month").startOf("day");
  return dernier.minus({ days: (dernier.weekday - numeroJour + 7) % 7 });
}

function jourCandidats(config, premierJour, dernierJour, pas) {
  const candidats = [];
  const joursSemaine = config.byDayOfWeek || [];
  const joursMois = config.byDayOfMonth || [];
  const rangs = config.byRankInMonth || [];
  if (config.frequency === "DAILY") {
    for (let j = premierJour; j <= dernierJour; j = j.plus({ days: pas })) candidats.push(j);
  } else if (config.frequency === "WEEKLY") {
    const jours = joursSemaine.length ? joursSemaine.map((js) => NUMERO_JOUR[js]) : [premierJour.weekday];
    for (let s = premierJour.startOf("week"); s <= dernierJour; s = s.plus({ weeks: pas })) {
      jours.forEach((n) => candidats.push(s.plus({ days: n - 1 })));
    }
  } else if (rangs.length) {
    for (let m = premierJour.startOf("month"); m <= dernierJour; m = m.plus({ months: pas })) {
      joursSemaine.forEach((js) => rangs.forEach((r) => candidats.push(jourDeRang(m, NUMERO_JOUR[js], Number(r)))));
    }
  } else {
    const quantiemes = joursMois.length ? joursMois.map(Number) : [premierJour.day];
    for (let m = premierJour.startOf("month"); m <= dernierJour; m = m.plus({ months: pas })) {
      quantiemes.filter((q) => q <= m.daysInMonth).forEach((q) => candidats.push(m.set({ day: q })));
    }
  }
  return candidats;
}

// Les jours où la répétition ouvrira un créneau, du jour du créneau au jour « jusqu'au »
// compris, comme l'API les calcule (CE-1392, CE-1416) : jamais le week-end. Les jours de
// fermeture du site, que l'écran ne connaît pas, sont retirés par l'API à l'enregistrement.
// `sautes` : les dates d'une répétition sur un jour du mois qui tombent un week-end ;
// `debut` et `fin` : le premier et le dernier jour de la répétition.
// Rend null quand la règle est incomplète (pas de fréquence, intervalle illisible) : c'est
// alors l'API qui répond.
export function occurrencesRepetition(config, debut) {
  if (!config || !config.frequency || !config.end || !debut) return null;
  const texte = String(config.interval ?? "").trim();
  if (!/^[0-9]+$/.test(texte) || Number(texte) < 1) return null;

  const premierJour = jour(debut).startOf("day");
  const dernierJour = jour(config.end).endOf("day");
  const joursSemaine = (config.byDayOfWeek || []).map((js) => NUMERO_JOUR[js]);
  const joursMois = (config.byDayOfMonth || []).map(Number);
  const retenus = jourCandidats(config, premierJour, dernierJour, Number(texte))
    .filter((j) => j >= premierJour && j <= dernierJour)
    .filter((j) => !joursSemaine.length || joursSemaine.includes(j.weekday))
    .filter((j) => !joursMois.length || joursMois.includes(j.day))
    .sort((a, b) => a.toMillis() - b.toMillis());

  const parJourDuMois = config.frequency === "MONTHLY" && !(config.byRankInMonth || []).length;
  return {
    dates: retenus.filter((j) => j.weekday <= 5),
    sautes: parJourDuMois ? retenus.filter((j) => j.weekday > 5) : [],
    debut: premierJour,
    fin: dernierJour.startOf("day"),
  };
}

const liste = (mots) => (mots.length > 1 ? mots.slice(0, -1).join(", ") + " et " + mots[mots.length - 1] : mots[0]);

// La répétition sur une même date, en une phrase : « Le 3 du mois », ou les jours du mois choisis
// en personnalisant (« Les 5 et 20 du mois »), avec ce que deviennent les dates d'un week-end.
export function phraseMemeDate(config, debut) {
  const choisis = (config.byDayOfMonth || []).map(Number).sort((a, b) => a - b);
  const quantiemes = choisis.length ? choisis : [jour(debut).day];
  const plusieurs = quantiemes.length > 1;
  return {
    ligne: `${plusieurs ? "Les" : "Le"} ${liste(quantiemes.map(quantieme))} du mois`,
    aide: plusieurs
      ? "Les mois où l'une de ces dates tombe un week-end, pas de créneau ce jour-là."
      : "Les mois où cette date tombe un week-end, pas de créneau.",
  };
}

// La règle en une phrase : « le premier mardi de chaque mois », « chaque semaine, le lundi et le jeudi ».
export function phraseRepetition(config, debut) {
  const pas = Number(config.interval) || 1;
  const joursSemaine = config.byDayOfWeek || [];
  if (config.frequency === "DAILY") {
    return pas === 1 ? "tous les jours ouvrés" : `un jour sur ${pas}, hors week-end`;
  }
  if (config.frequency === "WEEKLY") {
    const jours = (joursSemaine.length ? [...joursSemaine].sort((a, b) => NUMERO_JOUR[a] - NUMERO_JOUR[b]) : [jourSemaineDe(debut)]).map((js) => NOMS_JOURS[js]);
    return `${pas === 1 ? "chaque semaine" : `toutes les ${pas} semaines`}, ${liste(jours.map((j) => "le " + j))}`;
  }
  const rangs = config.byRankInMonth || [];
  let quoi;
  if (rangs.length) {
    const noms = rangs.map((r) => (RANGS_DANS_MOIS.find((x) => x.id === Number(r)) || {}).name);
    quoi = `le ${liste(noms)} ${liste(joursSemaine.map((js) => NOMS_JOURS[js]))}`;
  } else {
    const quantiemes = (config.byDayOfMonth || []).length ? [...config.byDayOfMonth].map(Number).sort((a, b) => a - b) : [jour(debut).day];
    quoi = `${quantiemes.length > 1 ? "les" : "le"} ${liste(quantiemes.map(quantieme))}`;
  }
  return quoi + (pas === 1 ? " de chaque mois" : `, tous les ${pas} mois`);
}
